import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

import { isAllowedRedirectUrl, navigateToAllowedUrl } from "../utils";
import { SuccessRedirect } from "../types";

interface Options {
  /** The partnership's `successRedirect` config, when it has one. */
  config?: SuccessRedirect;
  /** The countdown and auto-redirect only run while this is true. */
  enabled?: boolean;
  /** Navigation to run when the countdown elapses. Defaults to opening the URL. */
  onElapsed?: (url: string) => void;
}

/**
 * Where a partnership sends someone once their flow is done, plus the countdown
 * that takes them there. The `redirect_url` param wins over `defaultUrl`, and
 * both must pass the domain whitelist or the link is an open redirect. A
 * blocked URL reports to Sentry and resolves to no redirect at all.
 */
const usePartnershipRedirect = ({
  config,
  enabled = true,
  onElapsed,
}: Options) => {
  const url = useMemo(() => {
    if (!config) return "";

    // The default is ours, but it goes through the whitelist too: one path
    // to the sink.
    const rawRedirectUrl =
      new URLSearchParams(window.location.search).get("redirect_url") ||
      config.defaultUrl;
    if (!rawRedirectUrl) return "";

    if (!isAllowedRedirectUrl(rawRedirectUrl, config.allowedDomains)) {
      Sentry.captureException(
        new Error("Invalid redirect_url: blocked partnership redirect"),
        {
          extra: { rawRedirectUrl, allowedDomains: config.allowedDomains },
          tags: { security: "invalid_redirect_url" },
        },
      );
      return "";
    }

    return rawRedirectUrl;
  }, [config]);

  const redirect = config && url ? { ...config, url } : undefined;

  const autoRedirectMs = enabled ? redirect?.autoRedirectMs : undefined;

  // From the config, not the `enabled`-gated `autoRedirectMs`: a caller that
  // enables the countdown later is still on its first render here, and
  // `useState`'s initialiser never runs again — the counter would stay at 0.
  const [secondsLeft, setSecondsLeft] = useState(() =>
    config?.autoRedirectMs ? Math.ceil(config.autoRedirectMs / 1000) : 0,
  );

  // In refs so an inline arrow, and `config` (rebuilt every render by
  // getCompanyData), stay out of the effect deps: either restarts the timer
  // every render, so it never fires.
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;
  const allowedDomainsRef = useRef(config?.allowedDomains);
  allowedDomainsRef.current = config?.allowedDomains;

  useEffect(() => {
    if (!autoRedirectMs || !url) return;

    // Starting also resets, so a second entry begins at the top.
    setSecondsLeft(Math.ceil(autoRedirectMs / 1000));

    const timer = window.setTimeout(() => {
      const navigate = onElapsedRef.current;
      if (navigate) navigate(url);
      else navigateToAllowedUrl(url, allowedDomainsRef.current ?? []);
    }, autoRedirectMs);

    const interval = window.setInterval(
      () => setSecondsLeft((previous) => Math.max(0, previous - 1)),
      1000,
    );

    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
    };
  }, [autoRedirectMs, url]);

  return { redirect, secondsLeft };
};

export default usePartnershipRedirect;
